import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import type { AutomationTemplate } from '../engage-automations-tab/templates.types';
import type { AutomationTemplateContents } from '@reach/shared-types/automation-template';
import type {
  EventBasedTriggerMetadata,
  DateBasedTriggerMetadata,
} from '@reach/shared-types/business-automation';
import {
  actionTypeDefaults,
  checkActionNeedsSetup,
} from './actionTypeDefaults';

type TriggerMetadata = EventBasedTriggerMetadata | DateBasedTriggerMetadata;

type EditorContents = Omit<AutomationTemplateContents, 'triggerMetadata'> & {
  triggerMetadata: TriggerMetadata;
};

interface EditorState {
  localContents: EditorContents;
  isDirty: boolean;
  dirtySteps: {
    trigger: boolean;
    actions: Set<number>;
    settings: boolean;
  };
  hasReorderedOrAdded: boolean;
  hasStepsNeedingSetup: boolean;
}

function deepEqual<T>(a: T, b: T): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

type RestoreActionWithPreservedContent = (
  currentActionMap: Map<
    string | undefined,
    AutomationTemplateContents['actionData'][0]
  >,
  cleanAction: AutomationTemplateContents['actionData'][0],
  index: number,
) => AutomationTemplateContents['actionData'][0];

const restoreActionWithPreservedContent: RestoreActionWithPreservedContent = (
  currentActionMap,
  cleanAction,
  index,
) => {
  const currentAction = cleanAction.templateActionId
    ? currentActionMap.get(cleanAction.templateActionId)
    : undefined;

  const restoredAction = currentAction || cleanAction;
  return {
    ...restoredAction,
    index,
  };
};

interface SettingsUpdate {
  name: string;
  description: string;
  cooldownSeconds: number;
}

interface UseAutomationEditorReturn {
  state: EditorState;
  template: AutomationTemplate;
  updateTrigger: (metadata: TriggerMetadata) => void;
  undoTrigger: () => void;
  updateSettings: (settings: SettingsUpdate) => void;
  undoSettings: () => void;
  addAction: (type: string, index: number) => void;
  updateAction: (
    index: number,
    data: Partial<AutomationTemplateContents['actionData'][0]>,
  ) => void;
  undoAction: (index: number) => void;
  deleteAction: (index: number) => void;
  reorderActions: (fromIndex: number, toIndex: number) => void;
  undoReorder: () => void;
  markClean: () => void;
}

export function useAutomationEditor(
  template: AutomationTemplate,
): UseAutomationEditorReturn {
  const cleanContentsRef = useRef<AutomationTemplateContents>(
    template.contents,
  );

  const [localContents, setLocalContents] =
    useState<AutomationTemplateContents>(template.contents);

  useEffect(() => {
    cleanContentsRef.current = template.contents;
    setLocalContents(template.contents);
  }, [template.contents]);

  const computeDirtyState = useCallback(
    (contents: AutomationTemplateContents) => {
      const cleanContents = cleanContentsRef.current;
      const triggerIsDirty = !deepEqual(
        contents.triggerMetadata,
        cleanContents.triggerMetadata,
      );

      let cooldownChanged = false;
      if (
        contents.triggerMetadata?.triggerType === 'trigger_based' &&
        cleanContents.triggerMetadata?.triggerType === 'trigger_based'
      ) {
        cooldownChanged =
          contents.triggerMetadata.cooldownSeconds !==
          cleanContents.triggerMetadata.cooldownSeconds;
      }

      const settingsAreDirty =
        contents.name !== cleanContents.name ||
        contents.description !== cleanContents.description ||
        cooldownChanged;

      const currentActions = contents.actionData || [];
      const cleanActions = cleanContents.actionData || [];

      // Check if actions have been added or reordered
      const hasReorderedOrAdded =
        currentActions.length !== cleanActions.length ||
        currentActions.some(
          (action, index) =>
            !cleanActions[index] ||
            action.templateActionId !== cleanActions[index].templateActionId,
        );

      // Track dirty actions by comparing action content (not order)
      // We need to match actions by templateActionId to avoid marking reordered actions as dirty
      // Also exclude 'index' field from comparison since it changes on reorder
      const dirtyActionIndices = new Set<number>();
      const cleanActionMap = new Map(
        (cleanActions || []).map(action => [action.templateActionId, action]),
      );

      const compareActionContent = (
        current: (typeof currentActions)[0],
        clean: (typeof cleanActions)[0],
      ): boolean => {
        // Create copies without the 'index' field for comparison
        const { index: _currentIndex, ...currentContent } = current;
        const { index: _cleanIndex, ...cleanContent } = clean;
        return deepEqual(currentContent, cleanContent);
      };

      for (let i = 0; i < currentActions.length; i++) {
        const currentAction = currentActions[i];
        const cleanAction = cleanActionMap.get(currentAction.templateActionId);

        // If action doesn't exist in clean state, it's new (handled by hasReorderedOrAdded)
        // If it exists but content differs (excluding index), it's dirty
        if (cleanAction && !compareActionContent(currentAction, cleanAction)) {
          dirtyActionIndices.add(i);
        }
      }

      const isDirty =
        triggerIsDirty ||
        settingsAreDirty ||
        dirtyActionIndices.size > 0 ||
        hasReorderedOrAdded;

      return {
        isDirty,
        dirtySteps: {
          trigger: triggerIsDirty,
          actions: dirtyActionIndices,
          settings: settingsAreDirty,
        },
        hasReorderedOrAdded,
      };
    },
    [],
  );

  const { isDirty, dirtySteps } = useMemo(
    () => computeDirtyState(localContents),
    [localContents, computeDirtyState],
  );

  const { hasReorderedOrAdded } = useMemo(
    () => computeDirtyState(localContents),
    [localContents, computeDirtyState],
  );

  const hasStepsNeedingSetup = useMemo(() => {
    const actions = localContents.actionData || [];
    return actions.some(action => checkActionNeedsSetup(action));
  }, [localContents.actionData]);

  const state: EditorState = useMemo(
    () => ({
      localContents: localContents as EditorContents,
      isDirty,
      dirtySteps,
      hasReorderedOrAdded,
      hasStepsNeedingSetup,
    }),
    [
      localContents,
      isDirty,
      dirtySteps,
      hasReorderedOrAdded,
      hasStepsNeedingSetup,
    ],
  );

  const updateTrigger = (metadata: TriggerMetadata) => {
    setLocalContents(prev => ({
      ...prev,
      triggerMetadata: metadata,
    }));
  };

  const undoTrigger = () => {
    const cleanTrigger = cleanContentsRef.current.triggerMetadata;
    setLocalContents(prev => ({
      ...prev,
      triggerMetadata: cleanTrigger,
    }));
  };

  const updateSettings = ({
    name,
    description,
    cooldownSeconds,
  }: SettingsUpdate) => {
    setLocalContents(prev => {
      let triggerMetadata = prev.triggerMetadata;
      if (triggerMetadata?.triggerType === 'trigger_based') {
        triggerMetadata = {
          ...triggerMetadata,
          cooldownSeconds,
        };
      }

      return {
        ...prev,
        name,
        description,
        triggerMetadata,
      };
    });
  };

  const undoSettings = () => {
    const clean = cleanContentsRef.current;
    setLocalContents(prev => ({
      ...prev,
      name: clean.name,
      description: clean.description,
      triggerMetadata: clean.triggerMetadata,
    }));
  };

  const addAction = (type: string, index: number) => {
    setLocalContents(prev => {
      const newActionData = [...(prev.actionData || [])];
      const templateActionId = crypto.randomUUID();

      const actionDefaultData = actionTypeDefaults[type];
      if (!actionDefaultData) {
        return prev;
      }

      const newAction = {
        type: type as AutomationTemplateContents['actionData'][0]['type'],
        templateActionId,
        index,
        ...actionDefaultData,
      } as AutomationTemplateContents['actionData'][0];

      // Insert the new action at the specified index
      newActionData.splice(index, 0, newAction);

      // Update indices for all actions after the inserted one
      for (let i = index + 1; i < newActionData.length; i++) {
        newActionData[i] = {
          ...newActionData[i],
          index: i,
        };
      }

      return {
        ...prev,
        actionData: newActionData,
      };
    });
  };

  const updateAction = (
    index: number,
    data: Partial<AutomationTemplateContents['actionData'][0]>,
  ) => {
    setLocalContents(prev => {
      const newActionData = [...(prev.actionData || [])];
      if (newActionData[index]) {
        newActionData[index] = {
          ...newActionData[index],
          ...data,
        } as AutomationTemplateContents['actionData'][0];
      }
      return {
        ...prev,
        actionData: newActionData,
      };
    });
  };

  const undoAction = (index: number) => {
    const cleanAction = cleanContentsRef.current.actionData?.[index];
    if (!cleanAction) return;

    setLocalContents(prev => {
      const newActionData = [...(prev.actionData || [])];
      newActionData[index] = cleanAction;
      return {
        ...prev,
        actionData: newActionData,
      };
    });
  };

  const deleteAction = (index: number) => {
    setLocalContents(prev => {
      const newActionData = [...(prev.actionData || [])];
      newActionData.splice(index, 1);
      // Update indices for all actions after the deleted one
      for (let i = index; i < newActionData.length; i++) {
        newActionData[i] = {
          ...newActionData[i],
          index: i,
        };
      }
      return {
        ...prev,
        actionData: newActionData,
      };
    });
  };

  const reorderActions = (fromIndex: number, toIndex: number) => {
    setLocalContents(prev => {
      const newActionData = [...(prev.actionData || [])];
      const [removed] = newActionData.splice(fromIndex, 1);
      newActionData.splice(toIndex, 0, removed);
      // Update indices for all actions to ensure they're correct
      for (let i = 0; i < newActionData.length; i++) {
        newActionData[i] = {
          ...newActionData[i],
          index: i,
        };
      }
      return {
        ...prev,
        actionData: newActionData,
      };
    });
  };

  const undoReorder = () => {
    const cleanActions = cleanContentsRef.current.actionData || [];

    // Restore original order from clean state, but merge in content changes
    setLocalContents(prev => {
      const currentActions = prev.actionData || [];

      // Create a map of current actions by templateActionId to preserve content changes
      const currentActionMap = new Map(
        currentActions.map(action => [action.templateActionId, action]),
      );
      const restorePreservedAction = restoreActionWithPreservedContent.bind(
        null,
        currentActionMap,
      );

      return {
        ...prev,
        actionData: cleanActions.map(restorePreservedAction),
      };
    });
  };

  const markClean = () => {
    cleanContentsRef.current = localContents;
    // Force a re-render to update dirty state
    setLocalContents({ ...localContents });
  };

  return {
    state,
    template,
    updateTrigger,
    undoTrigger,
    updateSettings,
    undoSettings,
    addAction,
    updateAction,
    undoAction,
    deleteAction,
    reorderActions,
    undoReorder,
    markClean,
  };
}
